/* ============================================================
   Polish Gallery — motion layer (GSAP + ScrollTrigger)
   Header entrance; hero with a word-by-word heading, lights that
   string themselves across the top, a pointer-following glow and
   a slow photo drift; word-by-word section titles; promo cards
   that open up as they scroll in; rising cards, tiles and
   banners; photo parallax; counting stats; 3D tilt on category
   and product cards; magnetic buttons; the footer wordmark
   letter by letter; the product-page stage and the cart.
   Runs only when layout/theme.liquid has put .pg-motion on
   <html> (the visitor has not asked for reduced motion); a CSS
   failsafe reveals the hero if this never runs.
   ============================================================ */
(function () {
  'use strict';

  if (!document.documentElement.classList.contains('pg-motion') || !window.gsap) return;

  const gsap = window.gsap;
  const ST = window.ScrollTrigger;
  if (ST) gsap.registerPlugin(ST);
  const ease = 'power3.out';
  const canHover = window.matchMedia('(hover: hover)').matches;

  // Wrap each word (keeping <br> and inline tags) so it can rise out of its
  // own mask. Returns the inner spans.
  function splitWords(el) {
    if (el.dataset.pgSplit) return [];
    el.dataset.pgSplit = '1';
    const words = [];
    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === 3) {
          const frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(part));
              return;
            }
            const outer = document.createElement('span');
            const inner = document.createElement('span');
            outer.className = 'pg-w';
            inner.className = 'pg-w__i';
            inner.textContent = part;
            outer.appendChild(inner);
            frag.appendChild(outer);
            words.push(inner);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === 1 && child.tagName !== 'BR') {
          walk(child);
        }
      });
    };
    walk(el);
    return words;
  }

  function header() {
    const items = document.querySelectorAll('.pg-header .pg-logo, .pg-header .pg-search, .pg-header .pg-actions > *, .pg-nav__item');
    if (items.length) gsap.from(items, { opacity: 0, y: -14, duration: 0.7, ease, stagger: 0.04, clearProps: 'opacity,transform' });
  }

  function heroIntro() {
    const hero = document.querySelector('.pg-hero');
    if (!hero) return;
    const media = hero.querySelector('.pg-hero__media');
    // A slideshow zooms its own photos (pg-live.js), so only the frames drift here.
    const slides = hero.classList.contains('pg-hero--slides');
    const photo = slides ? null : hero.querySelector('.pg-hero__img, .pg-hero__glow');
    const heading = hero.querySelector('.pg-hero__heading');
    const words = heading ? splitWords(heading) : [];
    const others = Array.from(hero.querySelectorAll('.pg-hero__content > *')).filter((el) => el !== heading);
    const garland = hero.querySelector('.pg-garland');
    const glow = hero.querySelectorAll('.pg-bokeh, .pg-sparkles');

    const tl = gsap.timeline({ defaults: { ease, duration: 1 } });
    if (media) tl.to(media, { opacity: 1, duration: 1.2 }, 0);
    if (photo) tl.fromTo(photo, { scale: 1.14 }, { scale: 1, duration: 2.4, ease: 'power2.out' }, 0);
    if (garland) {
      tl.fromTo(garland, { clipPath: 'inset(0% 100% -60px 0%)' }, { clipPath: 'inset(0% 0% -60px 0%)', duration: 1.8, ease: 'power2.inOut', clearProps: 'clipPath' }, 0.3);
    }
    if (glow.length) tl.fromTo(glow, { opacity: 0 }, { opacity: 1, duration: 1.6, stagger: 0.25 }, 0.5);
    if (words.length) tl.fromTo(words, { yPercent: 115 }, { yPercent: 0, duration: 1.1, ease: 'power4.out', stagger: 0.08 }, 0.25);
    if (others.length) tl.fromTo(others, { opacity: 0, y: 36 }, { opacity: 1, y: 0, stagger: 0.12 }, 0.35);
    if (photo) tl.add(() => gsap.to(photo, { scale: 1.06, duration: 14, ease: 'sine.inOut', yoyo: true, repeat: -1 }));

    const drift = slides ? hero.querySelectorAll('.pg-hero__frame') : photo;
    if (ST && drift) {
      gsap.to(drift, {
        yPercent: 10,
        ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true },
      });
    }

    const cursor = hero.querySelector('.pg-hero__cursor');
    if (cursor && canHover) {
      const x = gsap.quickTo(cursor, 'x', { duration: 0.6, ease: 'power3' });
      const y = gsap.quickTo(cursor, 'y', { duration: 0.6, ease: 'power3' });
      hero.addEventListener('pointermove', (e) => {
        const r = hero.getBoundingClientRect();
        x(e.clientX - r.left);
        y(e.clientY - r.top);
        gsap.to(cursor, { opacity: 1, duration: 0.4, overwrite: 'auto' });
      });
      hero.addEventListener('pointerleave', () => gsap.to(cursor, { opacity: 0, duration: 0.6, overwrite: 'auto' }));
    }
  }

  // Section titles rise word by word the first time they are seen.
  function titles() {
    if (!ST) return;
    gsap.utils.toArray('.pg-title, .pg-ts__heading, .pg-lib__title, .pg-banner__heading, .pg-promo__title, .pg-article__title').forEach((el) => {
      const words = splitWords(el);
      if (!words.length) return;
      gsap.set(words, { yPercent: 115 });
      ST.create({
        trigger: el,
        start: 'top 90%',
        once: true,
        onEnter: () => gsap.to(words, { yPercent: 0, duration: 1, ease: 'power4.out', stagger: 0.06 }),
      });
    });
  }

  // Promo cards open up from a smaller window while their photo settles,
  // then drift a little as the page scrolls.
  function promos() {
    if (!ST) return;
    gsap.utils.toArray('.pg-promo').forEach((card, i) => {
      const img = card.querySelector('.pg-promo__img:not(.pg-promo__img--product)');
      const body = card.querySelectorAll('.pg-promo__body > *:not(.pg-promo__title)');
      const tl = gsap.timeline({ scrollTrigger: { trigger: card, start: 'top 86%', once: true }, delay: (i % 2) * 0.12 });
      tl.fromTo(card, { clipPath: 'inset(10% 8% 10% 8% round 14px)', opacity: 0 }, { clipPath: 'inset(0% 0% 0% 0% round 14px)', opacity: 1, duration: 1.2, ease: 'power3.inOut', clearProps: 'clipPath,opacity' });
      if (img) tl.fromTo(img, { scale: 1.3 }, { scale: 1.08, duration: 1.8, ease: 'power2.out' }, 0);
      if (body.length) tl.fromTo(body, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.8, stagger: 0.08, clearProps: 'opacity,transform' }, 0.35);
      if (!img) return;
      gsap.fromTo(img, { yPercent: -4 }, {
        yPercent: 4,
        ease: 'none',
        scrollTrigger: { trigger: card, start: 'top bottom', end: 'bottom top', scrub: true },
      });
      if (canHover) {
        card.addEventListener('pointerenter', () => gsap.to(img, { scale: 1.14, duration: 0.9, ease }));
        card.addEventListener('pointerleave', () => gsap.to(img, { scale: 1.08, duration: 0.9, ease }));
      }
    });
  }

  // Fade and rise elements as they scroll into view, a few at a time.
  function reveal(selector, { y = 40, scale = 1, stagger = 0.08 } = {}) {
    const els = gsap.utils.toArray(selector);
    if (!ST || !els.length) return;
    gsap.set(els, { opacity: 0, y, scale });
    ST.batch(els, {
      start: 'top 88%',
      once: true,
      onEnter: (batch) =>
        gsap.to(batch, { opacity: 1, y: 0, scale: 1, duration: 0.9, ease, stagger, overwrite: true, clearProps: 'transform,opacity' }),
    });
  }

  // Photos drift a little slower than the page inside their frames.
  function parallax(selector, amount) {
    if (!ST) return;
    gsap.utils.toArray(selector).forEach((img) => {
      gsap.set(img, { scale: 1.16 });
      gsap.fromTo(img, { yPercent: -amount }, {
        yPercent: amount,
        ease: 'none',
        scrollTrigger: { trigger: img.parentElement, start: 'top bottom', end: 'bottom top', scrub: true },
      });
    });
  }

  // "700+", "03", "4.7" count up from zero the first time they are seen.
  // Values with words in them ("3-Ply") are left alone.
  function countUp(el) {
    const node = Array.from(el.childNodes).reverse().find((n) => n.nodeType === 3 && /\d/.test(n.textContent));
    const match = node && node.textContent.match(/^(\s*\D*?)(\d+(?:\.\d+)?)([\s\S]*)$/);
    if (!match || /[a-z]/i.test(match[3])) return;
    const [, before, digits, after] = match;
    const decimals = (digits.split('.')[1] || '').length;
    const width = digits.startsWith('0') && !decimals ? digits.length : 0;
    const format = (v) => before + (width ? v.toFixed(0).padStart(width, '0') : v.toFixed(decimals)) + after;
    const counter = { v: 0 };
    node.textContent = format(0);
    gsap.to(counter, {
      v: parseFloat(digits),
      duration: 1.6,
      ease: 'power2.out',
      scrollTrigger: ST ? { trigger: el, start: 'top 92%', once: true } : undefined,
      onUpdate: () => { node.textContent = format(counter.v); },
    });
  }

  // Ornaments floating in a photo-less banner sway as if hanging from a branch.
  function sway() {
    gsap.utils.toArray('.pg-banner__orn').forEach((el, i) => {
      gsap.set(el, { transformOrigin: '50% 0%' });
      gsap.to(el, {
        rotation: gsap.utils.random(-7, 7),
        y: gsap.utils.random(-8, 8),
        duration: gsap.utils.random(2.2, 3.6),
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
        delay: i * 0.15,
      });
    });
  }

  // Cards lean toward the pointer in 3D.
  function tilt(selector, max) {
    if (!canHover) return;
    document.querySelectorAll(selector).forEach((el) => {
      const rx = gsap.quickTo(el, 'rotationX', { duration: 0.6, ease: 'power3' });
      const ry = gsap.quickTo(el, 'rotationY', { duration: 0.6, ease: 'power3' });
      el.addEventListener('pointerenter', () => gsap.set(el, { transformPerspective: 800 }));
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        ry(((e.clientX - r.left) / r.width - 0.5) * max);
        rx(-((e.clientY - r.top) / r.height - 0.5) * max);
      });
      el.addEventListener('pointerleave', () => { rx(0); ry(0); });
    });
  }

  // Buttons lean a little toward the pointer (full-width buttons stay put).
  function magnetic() {
    if (!canHover) return;
    document.querySelectorAll('.pg-btn').forEach((btn) => {
      if (btn.offsetWidth > 280) return;
      const x = gsap.quickTo(btn, 'x', { duration: 0.45, ease: 'power3' });
      const y = gsap.quickTo(btn, 'y', { duration: 0.45, ease: 'power3' });
      btn.addEventListener('pointermove', (e) => {
        const r = btn.getBoundingClientRect();
        x((e.clientX - r.left - r.width / 2) * 0.18);
        y((e.clientY - r.top - r.height / 2) * 0.3);
      });
      btn.addEventListener('pointerleave', () => { x(0); y(0); });
    });
  }

  // The giant footer wordmark rises letter by letter (as one piece if its
  // fill is painted through the text).
  function wordmark() {
    const el = document.querySelector('.pg-footer__word');
    if (!el || !ST || el.dataset.pgSplit) return;
    const style = getComputedStyle(el);
    const clipped = (style.backgroundClip || style.webkitBackgroundClip || '').includes('text');
    let targets = [el];
    if (!clipped) {
      el.dataset.pgSplit = '1';
      const text = el.textContent;
      el.textContent = '';
      targets = Array.from(text).map((ch) => {
        const span = document.createElement('span');
        span.className = 'pg-l';
        span.textContent = ch === ' ' ? ' ' : ch;
        el.appendChild(span);
        return span;
      });
    }
    gsap.set(targets, { yPercent: 60, opacity: 0 });
    ST.create({
      trigger: el,
      start: 'top 96%',
      once: true,
      onEnter: () => gsap.to(targets, { yPercent: 0, opacity: 1, duration: 1.1, ease: 'power4.out', stagger: 0.035 }),
    });
  }

  // Product page: the stage settles in, the details rise one by one and the
  // swatches pop in. The photo floats like a hanging ornament, leans toward
  // the pointer, and each newly chosen design fades in.
  function productPage() {
    const stage = document.querySelector('.pg-pdp__stage');
    if (!stage) return;
    const img = stage.querySelector('[data-main-image]');
    const info = document.querySelectorAll('.pg-pdp__info > *');
    const swatches = document.querySelectorAll('.pg-swatch');
    const tl = gsap.timeline({ defaults: { ease, duration: 0.9 } });
    tl.fromTo(stage, { opacity: 0, scale: 0.96 }, { opacity: 1, scale: 1, duration: 1.1, clearProps: 'transform,opacity' }, 0);
    if (info.length) tl.fromTo(info, { opacity: 0, y: 26 }, { opacity: 1, y: 0, stagger: 0.07, clearProps: 'transform,opacity' }, 0.15);
    if (swatches.length && swatches.length <= 40) {
      tl.fromTo(swatches, { opacity: 0, scale: 0.8 }, { opacity: 1, scale: 1, duration: 0.5, stagger: 0.03, clearProps: 'transform,opacity' }, 0.45);
    }
    if (!img) return;
    gsap.to(img, { y: -10, duration: 2.6, ease: 'sine.inOut', yoyo: true, repeat: -1 });
    let first = true;
    img.addEventListener('load', () => {
      if (first) { first = false; return; }
      gsap.fromTo(img, { opacity: 0, scale: 0.94 }, { opacity: 1, scale: 1, duration: 0.6, ease });
    });
    if (img.complete) first = false;
    if (!canHover) return;
    const x = gsap.quickTo(img, 'x', { duration: 0.8, ease: 'power3' });
    const r = gsap.quickTo(img, 'rotation', { duration: 0.8, ease: 'power3' });
    stage.addEventListener('pointermove', (e) => {
      const b = stage.getBoundingClientRect();
      const dx = (e.clientX - b.left) / b.width - 0.5;
      x(dx * 24);
      r(dx * 4);
    });
    stage.addEventListener('pointerleave', () => { x(0); r(0); });
  }

  // Cart: lines slide in, the summary rises and the shipping bar fills.
  function cartPage() {
    const items = document.querySelectorAll('.pg-cart__item');
    if (!items.length) return;
    const tl = gsap.timeline({ defaults: { ease, duration: 0.8 } });
    tl.fromTo(items, { opacity: 0, x: -30 }, { opacity: 1, x: 0, stagger: 0.08, clearProps: 'transform,opacity' }, 0);
    const summary = document.querySelector('.pg-cart__summary');
    if (summary) tl.fromTo(summary, { opacity: 0, y: 30 }, { opacity: 1, y: 0, clearProps: 'transform,opacity' }, 0.1);
    const bar = document.querySelector('.pg-ship__bar span');
    if (bar) tl.from(bar, { width: 0, duration: 1.4, ease: 'power2.out', clearProps: 'width' }, 0.3);
  }

  function init() {
    header();
    heroIntro();
    productPage();
    cartPage();
    titles();
    promos();
    reveal('.pg-lib__banner', { y: 30 });
    reveal('.pg-cat', { y: 36, stagger: 0.06 });
    reveal('.pg-card', { y: 40, stagger: 0.06 });
    reveal('.pg-ts__story', { y: 50 });
    reveal('.pg-banner', { y: 30, scale: 0.97 });
    // Inner pages
    reveal('.pg-page__body, .shopify-policy__body', { y: 30 });
    reveal('.pg-side', { y: 24, stagger: 0.06 });
    reveal('.pg-post', { y: 40, stagger: 0.06 });
    reveal('.pg-hit', { y: 30, stagger: 0.05 });
    reveal('.pg-blank', { y: 26 });
    reveal('.pg-404__inner > *', { y: 26, stagger: 0.07 });
    reveal('.pg-article__media, .pg-article__body', { y: 30 });
    tilt('.pg-post', 6);
    // Story photos stay whole (no parallax zoom): it cut off the tree topper tip (user 1 Oct).
    parallax('.pg-banner__img, .pg-lib__img', 7);
    document.querySelectorAll('.pg-stat__value').forEach(countUp);
    sway();
    tilt('.pg-cat', 10);
    tilt('.pg-card', 7);
    magnetic();
    wordmark();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
